import React, { useEffect, useRef, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { CardElementOptions } from "../../hooks/useIssuingElement";
import { useParams } from "react-router-dom";
import { Button } from "@mui/material";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { renderHighnoteCardFields } from "utils/cards/highnoteCardFields";
import { getPaymentCardTextStyle } from "@outpave/ds";
import CompanyPaymentCard from "components/common/cards/CompanyPaymentCard";
import { Stack } from "components/layout/reusableLayouts";
import { useBrandingContext } from "context/BrandingContext";

// This is where mobile comes to see a card!

interface ephemeral {
  verified: boolean;
  data: ephemeralData;
}

interface ephemeralData {
  issuerId: string;
  accountId: string;
  key: ephemeralKey;
}

interface ephemeralKey {
  id: string;
  secret: string;
}

const CardDetails: React.FC = () => {
  const { t } = useTranslation();
  const [message, setMessage] = useState("");
  const cardRef = useRef<HTMLDivElement>(null);
  const { isBrandingLoaded } = useBrandingContext();

  const params = useParams();
  let token = params.token;

  const handleClose = () => {
    window.ReactNativeWebView?.postMessage("close");
  };

  const verifyToken = async (token: string): Promise<ephemeral> => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/verify-token/${token}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error("Token verification failed");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return {
        verified: false,
        data: { issuerId: "", accountId: "", key: { id: "", secret: "" } },
      };
    }
  };

  const detailsAlreadyDisplayed = useRef(false);
  useEffect(() => {
    async function processCardData() {
      // The face has to be branded before it is read: the fields take concrete
      // values once and keep them, so mounting them first pins the default ink
      // for good.
      if (!isBrandingLoaded) return;
      if (detailsAlreadyDisplayed.current) return;
      detailsAlreadyDisplayed.current = true;
      if (!token) {
        setMessage(t("Error fetching card details"));
        return;
      }
      const tokenData = await verifyToken(token);
      if (!tokenData || tokenData.verified === false) {
        setMessage(t("Error fetching card details"));
        return;
      } else {
        setMessage("");
      }

      // Read here, not at render: the card is mounted and never remounts.
      const faceStyle = getPaymentCardTextStyle(cardRef.current) ?? undefined;
      const style: CardElementOptions = {
        style: { base: { ...faceStyle, fontSmoothing: "antialiased" } },
      };

      // TODO: if-else-if below can be improved to differentiate between Stripe and HN
      if (tokenData.data.key.id) {
        const stripe = await loadStripe(
          process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY as string,
          {
            stripeAccount: tokenData.data.accountId,
          },
        );

        try {
          if (!stripe) {
            throw new Error(
              "Could not connect to provider. Please try again later.",
            );
          }

          const elements = stripe.elements();

          const nonceResult = await stripe.createEphemeralKeyNonce({
            issuingCard: tokenData.data.issuerId,
          });
          const nonce = nonceResult.nonce;

          const number = elements.create("issuingCardNumberDisplay", {
            issuingCard: tokenData.data.issuerId,
            nonce: nonce,
            ephemeralKeySecret: tokenData.data.key.secret,
            style: style.style,
          });

          const cvc = elements.create("issuingCardCvcDisplay", {
            issuingCard: tokenData.data.issuerId,
            nonce: nonce,
            ephemeralKeySecret: tokenData.data.key.secret,
            style: style.style,
          });

          const exp = elements.create("issuingCardExpiryDisplay", {
            issuingCard: tokenData.data.issuerId,
            nonce: nonce,
            ephemeralKeySecret: tokenData.data.key.secret,
            style: style.style,
          });

          const cardNumberCopy = elements.create("issuingCardCopyButton", {
            toCopy: "number",
            style: style.style,
          });

          number.mount("#card-number");
          cvc.mount("#card-cvc");
          exp.mount("#card-exp");
          cardNumberCopy.mount("#card-number-copy");
          cardNumberCopy.on("click", () => {
            toast.success(t("Card number copied to clipboard"));
          });
        } catch (error) {
          console.error("An error occurred:", error);
          setMessage("An error occurred. Please try again.");
        }
      } else if (tokenData.data.key.secret) {
        try {
          await renderHighnoteCardFields({
            clientToken: tokenData.data.key.secret,
            paymentCardId: tokenData.data.issuerId,
            selectors: {
              cardNumber: "#card-number",
              cvv: "#card-cvc",
              expirationDate: "#card-exp",
            },
            styles: faceStyle,
            t,
          });
        } catch (error) {
          console.error("An error occurred:", error);
          setMessage(t("Error fetching card details"));
        }
      }
    }

    processCardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isBrandingLoaded]);

  return (
    <Stack
      gap="var(--space-2)"
      sx={{ alignItems: "center", marginBottom: "var(--space-9)" }}
    >
      <CompanyPaymentCard
        ref={cardRef}
        number={<div id="card-number" />}
        numberOverlay={<div id="card-number-copy" />}
        cvv={<div id="card-cvc" />}
        expiry={<div id="card-exp" />}
        overlay={message ? <span>{message}</span> : undefined}
      />
      <Button variant="primary-filled" onClick={handleClose}>
        {t("Close")}
      </Button>
    </Stack>
  );
};

export default CardDetails;
